import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, KeyRound, Mail, ShieldCheck, ChevronRight, Download, Smartphone } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  triggerInstall,
  subscribeInstallPrompt,
  isStandalone,
} from "@/components/PWAInstallPrompt";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AccountSection
 * - Sign out
 * - Change password (for email accounts; Google users can also "set a password"
 *   to enable email login as a backup)
 */
export default function AccountSection({ user }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [installHelp, setInstallHelp] = useState(false);

  useEffect(() => {
    return subscribeInstallPrompt((p) => setInstallPrompt(p));
  }, []);

  const isIOS = typeof window !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent || "") && !/CriOS|FxiOS|EdgiOS/i.test(navigator.userAgent || "");

  const handleInstall = async () => {
    if (installPrompt) {
      const res = await triggerInstall();
      if (res.outcome === "accepted") {
        toast.success("Installed!");
        setInstalled(true);
      }
      return;
    }
    // No native prompt available — show the manual hint dialog
    setInstallHelp(true);
  };

  const isGoogleUser = user?.auth_type === "google";
  // We assume any Google user who has never set a password doesn't have one.
  // (Backend will require current_password only if it finds a hash for the user.)
  const requiresCurrent = !isGoogleUser;

  const handleLogout = async () => {
    if (!window.confirm("Sign out of FlashSpecs?")) return;
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore — logout should always feel instant */ }
    localStorage.removeItem("session_token");
    window.location.href = "/";
  };

  const handleChangePassword = async (e) => {
    e?.preventDefault?.();
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: requiresCurrent ? current : null,
        new_password: next,
      });
      toast.success(isGoogleUser ? "Password set! You can now also sign in with email." : "Password updated");
      setOpen(false);
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update password");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="account-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <ShieldCheck size={18} className="text-[hsl(var(--primary))]" />
            Account
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Identity row */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(var(--secondary))]">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-[hsl(var(--primary))]/20 flex items-center justify-center">
                <span className="text-[hsl(var(--primary))] font-semibold">{user.name?.charAt(0).toUpperCase()}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user.name}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1 min-w-0">
                <Mail size={11} className="shrink-0" /> <span className="truncate">{user.email}</span>
              </p>
            </div>
            {isGoogleUser && (
              <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] shrink-0">
                Google
              </span>
            )}
          </div>

          {/* Change password */}
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="w-full flex items-center justify-between gap-3 p-3 rounded-lg bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--accent))] transition-colors text-left"
            data-testid="change-password-trigger"
          >
            <div className="flex items-center gap-3 min-w-0">
              <KeyRound size={16} className="text-[hsl(var(--primary))] shrink-0" />
              <div>
                <p className="text-sm font-medium">{isGoogleUser ? "Set a password" : "Change password"}</p>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))]">
                  {isGoogleUser ? "Optional — lets you also sign in with email" : "Update your sign-in password"}
                </p>
              </div>
            </div>
            <ChevronRight size={16} className="text-[hsl(var(--muted-foreground))] shrink-0" />
          </button>

          {/* Install app — only show when not already running standalone */}
          {!installed && (
            <button
              type="button"
              onClick={handleInstall}
              className="w-full flex items-center justify-between gap-3 p-3 rounded-lg bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--accent))] transition-colors text-left"
              data-testid="install-app-btn"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Smartphone size={16} className="text-[hsl(var(--primary))] shrink-0" />
                <div>
                  <p className="text-sm font-medium">Install app</p>
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))]">
                    {installPrompt ? "Add FlashSpecs to your home screen" : isIOS ? "iOS: Share → Add to Home Screen" : "Open in browser to install"}
                  </p>
                </div>
              </div>
              <Download size={16} className="text-[hsl(var(--muted-foreground))] shrink-0" />
            </button>
          )}

          {/* Sign out */}
          <Button
            variant="outline"
            onClick={handleLogout}
            className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-500/10 border-red-500/30"
            data-testid="account-signout-btn"
          >
            <LogOut size={16} className="mr-2" />
            Sign out
          </Button>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-sm" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>{isGoogleUser ? "Set a password" : "Change password"}</DialogTitle>
            <DialogDescription>
              {isGoogleUser
                ? "You currently sign in with Google. Setting a password gives you a backup way to log in."
                : "Enter your current password and choose a new one."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-3">
            {requiresCurrent && (
              <div className="space-y-1.5">
                <Label htmlFor="cp-current">Current password</Label>
                <Input
                  id="cp-current"
                  type="password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  autoComplete="current-password"
                  data-testid="cp-current"
                  required
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="cp-new">New password</Label>
              <Input
                id="cp-new"
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                placeholder="At least 6 characters"
                autoComplete="new-password"
                data-testid="cp-new"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-confirm">Confirm new password</Label>
              <Input
                id="cp-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                data-testid="cp-confirm"
                required
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
                data-testid="cp-submit"
              >
                {submitting ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Install help dialog — shown when no native prompt is available */}
      <Dialog open={installHelp} onOpenChange={setInstallHelp}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-sm" data-testid="install-help-dialog">
          <DialogHeader>
            <DialogTitle>Install FlashSpecs</DialogTitle>
            <DialogDescription>Add the app to your home screen for one-tap access and a full-screen experience.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-lg bg-[hsl(var(--secondary))] p-3">
              <p className="font-semibold mb-1">iPhone / iPad (Safari)</p>
              <ol className="list-decimal pl-4 text-[13px] text-[hsl(var(--muted-foreground))] space-y-0.5">
                <li>Tap the <span className="font-semibold text-[hsl(var(--foreground))]">Share</span> icon (square with up-arrow) in the toolbar.</li>
                <li>Scroll down and tap <span className="font-semibold text-[hsl(var(--foreground))]">Add to Home Screen</span>.</li>
                <li>Tap <span className="font-semibold text-[hsl(var(--foreground))]">Add</span> in the top-right.</li>
              </ol>
            </div>
            <div className="rounded-lg bg-[hsl(var(--secondary))] p-3">
              <p className="font-semibold mb-1">Android (Chrome / Edge / Samsung Internet)</p>
              <ol className="list-decimal pl-4 text-[13px] text-[hsl(var(--muted-foreground))] space-y-0.5">
                <li>Tap the browser menu (⋮ or three lines) in the top-right.</li>
                <li>Tap <span className="font-semibold text-[hsl(var(--foreground))]">Install app</span> or <span className="font-semibold text-[hsl(var(--foreground))]">Add to Home Screen</span>.</li>
                <li>Confirm to install.</li>
              </ol>
            </div>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] text-center">
              Brave / Firefox / DuckDuckGo: the install option may be hidden under "Add to Home screen" in the share menu.
            </p>
            <div className="flex justify-end pt-1">
              <Button onClick={() => setInstallHelp(false)}>Got it</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
